import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PriceAdvisorModal from "@/components/PriceAdvisorModal";
import { CheckCircle, CreditCard, Bank, Money, Coin } from "@phosphor-icons/react";

export default function Pricing() {
  const [courses, setCourses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => { api.get("/courses").then(r => setCourses(r.data)); }, []);

  function openModal(code = null) {
    setSelectedClass(code);
    setModalOpen(true);
  }

  return (
    <>
      <SEO title="Fiyatlar | Sürücü Kursu Ücretleri | Estem Gebze"
        description="2026 sürücü kursu fiyatları. B, A1, A2, A sınıfı ehliyet kursu ücretleri. Taksitli ödeme imkanı. Kişiselleştirilmiş fiyat alın."
        canonical="https://www.estemsurucukursu.com/fiyatlar"/>

      <section className="px-4 md:px-8 py-12 md:py-20 max-w-7xl mx-auto">
        <div className="nb-badge mb-4">Fiyatlar</div>
        <h1 className="font-heading font-bold text-5xl md:text-7xl tracking-tight leading-[1.05]">
          Size özel fiyat.<br/><span className="text-brand-primary">Saniyeler içinde.</span>
        </h1>
        <p className="mt-6 text-lg text-brand-muted max-w-2xl">Hangi sınıf, hangi durum? Birkaç soruyu yanıtlayın, size özel fiyatı hemen görelim. Tüm fiyatlarımız <strong>KDV dahil</strong>.</p>

        {/* Hero CTA */}
        <div className="mt-10 rounded-3xl bg-gradient-to-br from-brand-primary to-orange-600 p-8 md:p-12 text-white flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
          <div className="flex-1">
            <div className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-2">Ücretsiz · 1 Dakika</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Size özel fiyatınızı görün</h2>
            <p className="mt-2 text-white/80 text-sm">Kurs sınıfı ve durumunuza göre kişiselleştirilmiş fiyat. Taksit seçenekleri ve indirimler dahil.</p>
          </div>
          <button
            data-testid="hero-price-btn"
            onClick={() => openModal(null)}
            className="shrink-0 bg-white text-brand-primary font-bold text-base px-8 py-4 rounded-2xl hover:bg-orange-50 transition-colors flex items-center gap-2"
          >
            <Coin size={20} weight="fill"/> Fiyatları Gör
          </button>
        </div>

        {/* Course cards - price hidden */}
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c, i) => {
            const isPopular = !!c.popular;
            return (
              <div key={c.code} data-testid={`pricing-card-${c.code}`}
                className={`nb-card p-8 flex flex-col${isPopular ? " !bg-brand-primary text-white" : i % 2 === 0 ? " bg-white" : " bg-brand-primarySubtle"}`}>
                {isPopular && <div className="text-[10px] font-bold uppercase tracking-widest bg-white text-brand-primary px-3 py-1 rounded-full inline-block mb-3 self-start">EN ÇOK TERCİH EDİLEN</div>}
                <div className={`text-[11px] font-semibold tracking-[0.12em] uppercase ${isPopular ? "text-white/60" : "text-stone-500"}`}>{c.code}</div>
                <h3 className="font-heading font-bold text-2xl uppercase mt-1">{c.name}</h3>
                <div className={`mt-2 text-sm ${isPopular ? "text-white/70" : "text-stone-500"}`}>{c.duration}</div>
                <div className="mt-6 space-y-2 text-sm flex-1">
                  {["MEB onaylı sertifika","E-sınav harcı destek","Tüm materyaller dahil","Taksit imkanı"].map(t => (
                    <div key={t} className="flex items-center gap-2"><CheckCircle weight="fill"/>{t}</div>
                  ))}
                </div>
                <button
                  data-testid={`price-btn-${c.code}`}
                  onClick={() => openModal(c.code)}
                  className={`mt-6 w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider border rounded-full py-3 transition-colors ${isPopular ? "bg-white text-brand-primary hover:bg-orange-50" : "bg-brand-ink text-white hover:bg-brand-inkLight border-transparent"}`}
                >
                  <Coin size={16} weight="fill"/> Fiyatları Gör
                </button>
              </div>
            );
          })}
        </div>

        {/* Payment methods */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { i: CreditCard, t: "Iyzico ile Taksit", d: "Tüm kartlara 3, 6, 9 taksit imkanı." },
            { i: Bank, t: "Banka Havalesi", d: "EFT/Havale ile peşin ödeme indirimi." },
            { i: Money, t: "Ofiste Nakit", d: "Gebze ofisimizde nakit ödeme." },
          ].map((x,i)=>(
            <div key={i} className="p-6 border border-stone-200 rounded-3xl bg-white">
              <x.i size={36} weight="duotone" className="text-brand-primary mb-3"/>
              <h3 className="font-heading font-bold text-xl uppercase">{x.t}</h3>
              <p className="text-brand-muted text-sm mt-2">{x.d}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 p-8 rounded-3xl border-2 border-dashed border-stone-300 text-center">
          <p className="text-stone-500 text-sm mb-3">Hâlâ kararsız mısınız? Danışmanımıza bağlanın.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => openModal(null)} data-testid="bottom-price-btn"
              className="nb-btn !bg-brand-primary !text-white !px-6 !py-3">
              Fiyatları Gör
            </button>
            <Link to="/iletisim" className="nb-btn !bg-white !text-brand-ink !border-stone-300 !px-6 !py-3">
              Bize Ulaşın
            </Link>
          </div>
        </div>
      </section>

      <PriceAdvisorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultClass={selectedClass}
      />
    </>
  );
}
